'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getAdService } from './AdService';
import type { AdStatus, AdProviderId } from './types';

const AdContext = createContext<{
  status: AdStatus;
  open: (providerId?: AdProviderId) => Promise<void>;
  currentProvider: AdProviderId | null;
} | null>(null);

type AdProps = {
  /** 廣告商配置 */
  providers: {
    gigapub?: { projectId: string | number };
    monetag?: { projectId: string | number;[key: string]: any };
    onclicka?: { projectId: string | number;[key: string]: any };
  };
  children: React.ReactNode;
};

export function Ad({ providers, children }: AdProps) {
  const [status, setStatus] = useState<AdStatus>('idle');
  const [currentProvider, setCurrentProvider] = useState<AdProviderId | null>(null);
  const service = useMemo(() => getAdService(), []);

  // 註冊廣告完成事件處理
  useEffect(() => {
    const unregister = service.onCompleted((event) => {
      console.log('[AdProvider] Ad completed event received:', {
        provider: event.provider,
        timestamp: new Date(event.timestamp).toISOString(),
        hasData: !!event.data,
      });
    });

    return () => {
      unregister();
    };
  }, [service]);

  useEffect(() => {
    // 註冊所有提供的廣告商
    if (providers.gigapub) {
      service.registerProvider('gigapub', providers.gigapub);
    }
    if (providers.monetag) {
      service.registerProvider('monetag', providers.monetag);
    }
    if (providers.onclicka) {
      service.registerProvider('onclicka', providers.onclicka);
    }

    // 初始化所有廣告商
    service.initAll().then(() => {
      const provider = service.getCurrentProvider();
      setStatus(provider?.getStatus() || 'idle');
      setCurrentProvider(provider?.providerId || null);
    });
  }, []);

  const open = useCallback(async (providerId?: AdProviderId) => {
    try {
      const data = await service.open(providerId);

      // 觸發全局事件
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('ad:completed', {
          detail: {
            provider: providerId ?? currentProvider,
            data,
          },
        }));
      }

      return data;
    } catch (error) {
      console.error('[AdProvider] Failed to open ad:', error);
      throw error;
    }
  }, [service]);

  const value = useMemo(() => ({
    status,
    open,
    currentProvider,
  }), [status, open, currentProvider]);

  return (
    <AdContext.Provider value={value}>
      {children}
      {providers.onclicka?.spotId && <div data-banner-id={providers.onclicka.spotId}></div>}
    </AdContext.Provider>
  );
}

export function useAd() {
  const ctx = useContext(AdContext);
  if (!ctx) {
    throw new Error('useAd must be used within AdProvider');
  }
  return ctx;
}
